import type { GroupedProductEntry } from './reviewListingGroups';

export function stripGroupFromReviewItems<T extends { id: string; products: any[] }>(
  items: T[],
  group: GroupedProductEntry
): T[] {
  const toRemove = new Set(group.allProductsInGroup.map(e => `${e.parentId}:${e.index}`));
  return items
    .map(item => {
      const updatedProducts = item.products.filter((_, idx) => !toRemove.has(`${item.id}:${idx}`));
      if (updatedProducts.length === 0) return null;
      return { ...item, products: updatedProducts };
    })
    .filter((item): item is T => item !== null);
}

export async function postMarkGroupReviewed(opts: {
  group: GroupedProductEntry;
  reviewState: string;
  post: (parentId: string, params: Record<string, string>) => Promise<unknown>;
}): Promise<void> {
  for (const { parentId, product } of opts.group.allProductsInGroup) {
    const listingId = (product as any)?.listing_id;
    const url = product?.url || (product as any)?.current_url;
    if (!listingId && !url) continue;
    const params: Record<string, string> = { review_state: opts.reviewState };
    if (listingId) params.listing_id = String(listingId);
    else if (url) params.listing_url = String(url);
    await opts.post(parentId, params);
  }
}

/**
 * Hold the crawl-result lock until `fn` finishes. Callers that share a parent
 * must run one at a time: mark-reviewed reads the listings JSON and writes it back.
 * Acquire parent ids in sorted order when a group spans more than one result.
 */
async function withParentLocks(
  parentIds: string[],
  tails: Map<string, Promise<void>>,
  fn: () => Promise<void>
): Promise<void> {
  const ids = [...new Set(parentIds)].sort();
  const acquire = async (index: number): Promise<void> => {
    if (index >= ids.length) {
      await fn();
      return;
    }
    const parentId = ids[index];
    const prev = tails.get(parentId) ?? Promise.resolve();
    let release!: () => void;
    const gate = new Promise<void>(resolve => {
      release = resolve;
    });
    tails.set(
      parentId,
      prev.then(() => gate)
    );
    await prev;
    try {
      await acquire(index + 1);
    } finally {
      release();
    }
  };
  await acquire(0);
}

/** Mark many review groups. Updates that share a crawl result stay serialized. */
export async function postMarkGroupsReviewed(opts: {
  groups: GroupedProductEntry[];
  reviewState: string;
  post: (parentId: string, params: Record<string, string>) => Promise<unknown>;
  concurrency?: number;
}): Promise<{ marked: GroupedProductEntry[]; failed: GroupedProductEntry[] }> {
  const marked: GroupedProductEntry[] = [];
  const failed: GroupedProductEntry[] = [];
  if (opts.groups.length === 0) return { marked, failed };

  const tails = new Map<string, Promise<void>>();
  const concurrency = Math.max(1, Math.min(opts.concurrency ?? 4, opts.groups.length));
  let next = 0;

  const worker = async () => {
    while (next < opts.groups.length) {
      const index = next;
      next += 1;
      const group = opts.groups[index];
      const parentIds = group.allProductsInGroup.map(entry => entry.parentId);
      try {
        await withParentLocks(parentIds, tails, () =>
          postMarkGroupReviewed({
            group,
            reviewState: opts.reviewState,
            post: opts.post,
          })
        );
        marked.push(group);
      } catch (err) {
        failed.push(group);
        console.error('Failed to mark held listing reviewed:', err);
      }
    }
  };

  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  return { marked, failed };
}
